import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { App, Button, Input, Select, Space } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import type { ColumnsType } from "antd/es/table";
import { listResource, deleteResource } from "../../api/resourceApi";
import { DataGrid } from "../../shared/components/DataGrid";
import { confirmDeletePreview } from "../../shared/components/ConfirmDeletePreview";
import { PageHeaderActions } from "../../shared/components/PageHeaderActions";
import { useEntityFormLauncher } from "../../shared/entityForms/EntityFormLauncherHost";
import { getApiErrorMessage } from "../../shared/utils/apiError";
import type { ResourceConfig } from "./resourceConfigs";

export function ResourcePage({ config }: { config: ResourceConfig }) {
  const openForm = useEntityFormLauncher();
  const { message } = App.useApp();
  const [partySearch, setPartySearch] = useState("");
  const [partySort, setPartySort] = useState("name-asc");
  const query = useQuery({
    queryKey: ["resource", config.listUrl, config.listMethod],
    queryFn: () => listResource<any>(config.listUrl, config.listMethod)
  });

  const columns: ColumnsType<any> = [
    ...config.columns,
    {
      key: "actions",
      title: "",
      width: 92,
      fixed: "right",
      render: (_, row) => (
        <Space size={4}>
          {config.createModule && (config.canEditRow?.(row) ?? true) ? (
            <Button size="small" icon={<EditOutlined />} onClick={() => openForm({ module: config.createModule!, mode: "edit", id: row.id, source: "grid-edit", onSuccess: () => query.refetch() })} />
          ) : null}
          {config.deleteUrl && (config.canDeleteRow?.(row) ?? true) ? (
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => {
              confirmDeletePreview({
                entityName: config.title,
                fields: [
                  { label: "Name", value: row.name ?? row.fullName ?? row.code ?? row.sacCode ?? row.label ?? row.id },
                  { label: "Code", value: row.code ?? row.sacCode },
                  { label: "Type", value: row.type ?? row.accountType ?? row.taxType, type: "tag" },
                  { label: "Active", value: row.isActive === undefined ? undefined : row.isActive ? "Yes" : "No", type: "tag" }
                ],
                onConfirm: async () => {
                  try {
                    await deleteResource(config.deleteUrl!, row.id);
                    message.success("Deleted");
                    await query.refetch();
                  } catch (err) {
                    message.error(getApiErrorMessage(err, "Unable to delete"));
                  }
                }
              });
            }} />
          ) : null}
        </Space>
      )
    }
  ];
  const rows = useMemo(() => {
    const source = query.data ?? [];
    if (config.title !== "Party") return source;
    const needle = partySearch.trim().toLocaleLowerCase();
    const filtered = needle
      ? source.filter((row: any) => [row.name, row.code, row.type, row.gstNumber, row.pan, row.phone, row.email, row.city]
          .some((value) => String(value ?? "").toLocaleLowerCase().includes(needle)))
      : [...source];
    return filtered.sort((left: any, right: any) => {
      if (partySort === "name-desc") return String(right.name ?? "").localeCompare(String(left.name ?? ""));
      if (partySort === "type") return String(left.type ?? "").localeCompare(String(right.type ?? "")) || String(left.name ?? "").localeCompare(String(right.name ?? ""));
      if (partySort === "balance-desc") {
        const leftBalance = Math.abs(Number(left.debitTotal ?? 0) - Number(left.creditTotal ?? 0));
        const rightBalance = Math.abs(Number(right.debitTotal ?? 0) - Number(right.creditTotal ?? 0));
        return rightBalance - leftBalance;
      }
      return String(left.name ?? "").localeCompare(String(right.name ?? ""));
    });
  }, [config.title, partySearch, partySort, query.data]);

  return (
    <div>
      <PageHeaderActions
        title={config.title}
        subtitle={config.subtitle}
        right={
          <>
            {config.createModule ? <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm({ module: config.createModule!, mode: "create", source: "page-create", onSuccess: () => query.refetch() })}>Create</Button> : null}
            <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} />
          </>
        }
      />
      {config.title === "Party" ? (
        <div className="resource-list-toolbar">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search name, GSTIN, PAN, phone, email or city"
            value={partySearch}
            onChange={(event) => setPartySearch(event.target.value)}
          />
          <Select
            value={partySort}
            onChange={setPartySort}
            options={[
              { value: "name-asc", label: "Name A-Z" },
              { value: "name-desc", label: "Name Z-A" },
              { value: "type", label: "Party Type" },
              { value: "balance-desc", label: "Highest Balance" }
            ]}
          />
        </div>
      ) : null}
      <DataGrid rows={rows} columns={columns} loading={query.isLoading} tableId={`resource-${config.title}`} />
    </div>
  );
}
